// Pure helpers behind <FieldInput> / <FieldsForm>: converting between what an
// input holds (strings) and the typed values @dunea/plugin-sdk validates.
// No React imports so they stay unit-testable.

import { currencyExponent, minorToMajorInput } from '~/components/workbench/tools/payments/payments-model';
import {
  FIELD_TYPES,
  MAX_JSON_BYTES,
  validateFields,
  type FieldDefinition,
  type FieldType,
  type MoneyValue,
} from './sdk';

export const REFERENCE_TYPES: readonly FieldType[] = [
  'product_reference',
  'variant_reference',
  'collection_reference',
  'metaobject_reference',
];

export function isReference(type: FieldType): boolean {
  return REFERENCE_TYPES.includes(type);
}

/** Owner-facing name of a field type ("One line of text"). */
export function fieldTypeLabel(type: FieldType): string {
  return FIELD_TYPES[type] ?? type;
}

/** Field types an owner can pick when defining a content type, in menu order. */
export const PICKABLE_TYPES: readonly FieldType[] = [
  'single_line_text',
  'multi_line_text',
  'rich_text',
  'image',
  'url',
  'email',
  'integer',
  'decimal',
  'money',
  'boolean',
  'date',
  'date_time',
  'color',
  'select',
  'product_reference',
  'collection_reference',
  'metaobject_reference',
  'page_reference',
  'json',
];

/** Empty strings, null and empty lists count as "not set". */
export function isEmptyValue(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Values without the unset ones (what gets sent to studio). */
export function pruneEmpty(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).filter(([, v]) => !isEmptyValue(v)));
}

/** A number input's text → number | undefined (NaN never escapes). */
export function parseNumberInput(text: string): number | undefined {
  if (text.trim() === '') {
    return undefined;
  }

  const n = Number(text);

  return Number.isFinite(n) ? n : undefined;
}

/** ISO date-time → the value of an <input type="datetime-local"> (local time, minutes). */
export function isoToLocalInput(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) {
    return '';
  }

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const pad = (n: number) => String(n).padStart(2, '0');

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO 8601 (UTC), undefined when empty/invalid. */
export function localInputToIso(local: string): string | undefined {
  if (!local) {
    return undefined;
  }

  const d = new Date(local);

  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Money as an owner types it: major units ("12.50") plus a currency. */
export function moneyToInput(v: unknown): { amount: string; currency: string } {
  const m = v as Partial<MoneyValue> | null;

  if (!m || typeof m !== 'object' || typeof m.currency !== 'string') {
    return { amount: '', currency: '' };
  }

  return {
    amount: Number.isInteger(m.amount) ? minorToMajorInput(m.amount as number, m.currency) : '',
    currency: m.currency,
  };
}

/**
 * The owner's major amount + currency → MoneyValue in minor units. Undefined
 * when the amount is empty; `null` when it can't be read (shown as an error).
 */
export function inputToMoney(amount: string, currency: string): MoneyValue | undefined | null {
  const clean = amount.trim().replace(/,/g, '');

  if (!clean) {
    return undefined;
  }

  const code = currency.trim().toUpperCase();
  const exp = /^[A-Z]{3}$/.test(code) ? currencyExponent(code) : 2;
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(clean);

  if (!match || !/^[A-Z]{3}$/.test(code) || (match[3] ?? '').length > exp) {
    return null;
  }

  const [, sign, whole, frac = ''] = match;
  const minor = Number(whole) * 10 ** exp + Number(frac.padEnd(exp, '0') || '0');

  return Number.isSafeInteger(minor) ? { amount: sign ? -minor : minor, currency: code } : null;
}

/** JSON textarea text → value. */
export function parseJsonInput(text: string): { ok: true; value: unknown } | { ok: false; error: string } {
  if (!text.trim()) {
    return { ok: true, value: undefined };
  }

  try {
    const value = JSON.parse(text) as unknown;

    return text.length > MAX_JSON_BYTES ? { ok: false, error: 'is larger than 16 KB' } : { ok: true, value };
  } catch {
    return { ok: false, error: 'is not valid JSON' };
  }
}

/** Field errors for the current draft (empties pruned), keyed by field key. Empty when valid. */
export function draftErrors(
  fields: readonly FieldDefinition[],
  values: Record<string, unknown>,
): Record<string, string> {
  const result = validateFields(fields, pruneEmpty(values));
  return result.ok ? {} : result.errors;
}

/** A short one-line preview of a value for lists and tables. */
export function previewValue(field: FieldDefinition | undefined, value: unknown): string {
  if (isEmptyValue(value)) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map((v) => previewValue(field ? { ...field, list: false } : undefined, v)).join(', ');
  }

  if (typeof value === 'string') {
    if (field?.type === 'select') {
      return field.options?.find((o) => o.value === value)?.label ?? value;
    }

    return value.length > 80 ? `${value.slice(0, 79)}…` : value;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (field?.type === 'money') {
    const m = moneyToInput(value);
    return m.amount ? `${m.amount} ${m.currency}` : '';
  }

  if (field?.type === 'image') {
    const img = value as { alt?: string; url?: string };
    return img.alt || img.url || '';
  }

  const text = JSON.stringify(value);

  return text.length > 80 ? `${text.slice(0, 79)}…` : text;
}
